import { useEffect, useState } from 'react';
import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { strings } from '@singparty/shared';
import { meQuery } from '@/lib/authQuery';
import { apiGet, apiPost, ApiClientError } from '@/lib/apiClient';
import { queryClient } from '@/lib/queryClient';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/ui/Logo';

export const Route = createFileRoute('/singnow')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me) throw redirect({ to: '/host/dashboard' });
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);

  // The lock lives in a signed cookie, so the server is the one that decides
  // whether the form renders at all — a reload can't shake it off.
  useEffect(() => {
    let cancelled = false;
    void apiGet<{ locked: boolean }>('/api/auth/login-status')
      .then((r) => !cancelled && setLocked(r.locked))
      .catch(() => {
        /* leave the form up; the POST enforces the lock regardless */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const me = await apiPost<{ email: string }>('/api/auth/login', { email: email.trim() });
      // Replace the cached identity outright rather than invalidating it. Route
      // guards read `me` through ensureQueryData, which serves cached data even
      // when stale, and an invalidate only refetches queries that have an active
      // observer — `me` has none. Invalidating would leave /host's guard reading
      // the pre-login `null` and bouncing straight back here.
      queryClient.clear(); // also drops anything cached for a previous host
      queryClient.setQueryData(meQuery.queryKey, me);
      await router.invalidate(); // re-run guards against the new identity
      await navigate({ to: '/host/dashboard' });
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'Sign-in failed';
      setFormError(msg);
      if (msg === strings.auth.lockedOut) setLocked(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-10 text-center">
          <Logo className="mx-auto" />
          <p className="mt-2 text-text-secondary">{strings.tagline}</p>
        </div>

        {locked ? (
          <p role="alert" className="text-center text-sm text-text-secondary">
            {strings.auth.lockedOut}
          </p>
        ) : (
          <form onSubmit={signIn}>
            <label htmlFor="host-email" className="mb-2 block text-sm text-text-secondary">
              {strings.auth.hostEmail}
            </label>
            <input
              id="host-email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 w-full rounded-pill border border-border bg-surface px-4 text-sm outline-none focus-visible:border-primary"
            />
            <Button type="submit" loading={busy} size="lg" className="mt-3 w-full">
              {strings.auth.signIn}
            </Button>
            {formError && (
              <p role="alert" className="mt-3 text-sm text-danger">
                {formError}
              </p>
            )}
          </form>
        )}

        <p className="mt-10 text-center text-xs text-text-secondary">{strings.auth.inviteOnly}</p>
      </div>
    </div>
  );
}
